import React from 'react';
import {
  MapPin,
  Clock,
  Eye,
  Filter,
  Paperclip,
  Edit3
} from 'lucide-react';

const IncidentsList = ({
  incidents,
  incidentFilters,
  setIncidentFilters,
  onLoadIncidents,
  onViewIncident,
  onEditIncident,
  getSeverityColor,
  getStatusColor,
  getRiskColor
}) => {
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center flex-shrink-0">
            <Filter className="w-4 h-4 mr-2 text-gray-500 dark:text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filters:</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <select
              value={incidentFilters.status}
              onChange={(e) => setIncidentFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md text-base sm:text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="reported">Reported</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>

            <select
              value={incidentFilters.severity}
              onChange={(e) => setIncidentFilters(prev => ({ ...prev, severity: e.target.value }))}
              className="w-full px-3 py-3 sm:py-2 border border-gray-300 dark:border-gray-600 rounded-md text-base sm:text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Severities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>

            <label className="flex items-center text-sm sm:text-base lg:text-sm col-span-1 lg:col-span-2 py-2 lg:py-0">
              <input
                type="checkbox"
                checked={incidentFilters.my_incidents}
                onChange={(e) => setIncidentFilters(prev => ({ ...prev, my_incidents: e.target.checked }))}
                className="mr-3 h-5 w-5 sm:h-4 sm:w-4 rounded text-red-600 focus:ring-red-500 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-gray-700 dark:text-gray-300">My Incidents Only</span>
            </label>
          </div>
          <div className="flex justify-end">
            <button
              onClick={onLoadIncidents}
              className="w-full sm:w-auto px-6 py-3 sm:py-2 bg-blue-600 text-white rounded-md text-base sm:text-sm hover:bg-blue-700 flex-shrink-0 whitespace-nowrap min-h-[44px] sm:min-h-0"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Incidents List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
            Recent Incidents ({incidents.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {incidents.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No incidents found. Click "Report Incident" to create one.
            </div>
          ) : (
            incidents.map((incident) => (
              <div key={incident.id} className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <div className="flex flex-col gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                      <h4 className="font-medium text-base sm:text-lg text-gray-900 dark:text-white">
                        {incident.title}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(incident.severity)}`}>
                          {incident.severity}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(incident.status)}`}>
                          {incident.status}
                        </span>
                        {incident.risk_score && (
                          <span className={`px-2 py-1 text-xs rounded-full ${getRiskColor(incident.risk_score)}`}>
                            Risk: {incident.risk_score}
                          </span>
                        )}
                      </div>
                    </div>

                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-3 line-clamp-2">
                      {incident.description}
                    </p>

                    <div className="flex flex-col sm:flex-row sm:items-center text-xs sm:text-sm text-gray-500 dark:text-gray-400 gap-2 sm:gap-4">
                      <span className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="truncate">{incident.location_description}</span>
                      </span>
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                        {new Date(incident.created_at).toLocaleDateString()}
                      </span>
                      {incident.attachments && incident.attachments.length > 0 && (
                        <span className="flex items-center">
                          <Paperclip className="w-3 h-3 mr-1 flex-shrink-0" />
                          {incident.attachments.length} attachment(s)
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <button
                      onClick={() => onViewIncident(incident)}
                      className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors min-h-[44px] sm:min-h-0"
                      title="View Incident Details"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">View</span>
                    </button>
                    <button
                      onClick={() => onEditIncident(incident)}
                      className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-md transition-colors min-h-[44px] sm:min-h-0"
                      title="Edit Incident"
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Edit</span>
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default IncidentsList;
